/* Content editor (spec 02, écrans B/C/D). Center canvas + right sidechat */
/* (open by default, localStorage, mobile drawer), export PDF, read-only meta, */
/* archived banner + read-only, live bareme total. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type Editor } from '@tiptap/core';
import { useParams, useNavigate } from '../lib/routing';
import { HeaderContent, APP_HEADER_HEIGHT_VAR } from '../components/shell/app-header';
import ContentSectionsEditor, { variantAwareSectionsRepository, type ContentSectionRow } from '../components/content-sections-editor';
import VariantTabs, { type VariantDeclaration } from '../components/variant-tabs';
import BootstrapProgress from '../components/bootstrap-progress';
import { useBootstrapJob } from '../hooks/use-bootstrap-job';
import { useDocumentChat } from '../hooks/use-document-chat';
import { ActiveEditorCtx, RichToolbar } from '../components/rich-doc-editor';
import SendAssignmentModal from '../components/send-assignment-modal';
import { DocumentSidechat } from '../components/document-sidechat';
import { api } from '../lib/api';
import type { ContentDocumentKind } from '../lib/editor-chat';
import { Button, buttonVariants } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  DotsThreeVertical, Sparkle, Eye, Archive, ChatCircle, ChartBar, ArrowUUpLeft, Check, SidebarSimple,
  FilePdf, Key, CircleNotch, UploadSimple,
} from '@phosphor-icons/react';
import PdfPanel from '../components/pdf-panel';
import { EvaluationPanel, baremeOf, NOTED_TYPES } from '../components/evaluation-panel';
import AuthoringWarningsList, { type AuthoringWarningRow } from '../components/authoring-warnings';

type Kind = ContentDocumentKind;
interface Doc {
  id: string; kind: Kind; title: string; level_id: string; subject_id: string;
  global_correction_rules?: string | null; archived_at: string | null; sections?: ContentSectionRow[];
  variants?: readonly VariantDeclaration[] | null;
}
interface Ref { id: string; label: string }

const KIND_LABELS: Record<Kind, string> = { general: 'Document', course: 'Cours', exercice_sheet: 'Exercice', exam: 'Évaluation' };
const SIDECHAT_KEY = 'naia.editor.sidechat.open';
const SIDECHAT_WIDTH_KEY = 'naia.editor.sidechat.width';
const MIN_SIDECHAT_WIDTH = 280;
const MAX_SIDECHAT_WIDTH = 560;
const DEFAULT_SIDECHAT_WIDTH = 380;
const CONTENT_MIN_WIDTH = 320;
const RESIZE_HANDLE_WIDTH = 11;
const clampSidechatWidth = (w: number, max = MAX_SIDECHAT_WIDTH) => Math.max(MIN_SIDECHAT_WIDTH, Math.min(max, w));
type PdfVariant = 'subject' | 'answer_key';
type PdfMode = 'view' | 'download';
type PdfJob = `${PdfVariant}-${PdfMode}`;
const PDF_VARIANT_LABELS: Record<PdfVariant, string> = { subject: 'Sujet', answer_key: 'Corrigé' };

/* Nom de fichier lisible : « Fractions — Corrigé.pdf ». */
const pdfFileName = (title: string, variant: PdfVariant) =>
  `${title.replace(/[/\\?%*:|"<>]/g, '-').trim() || 'document'} — ${PDF_VARIANT_LABELS[variant]}.pdf`;

const sectionBareme = (s: ContentSectionRow): number => baremeOf(s) ?? 0;

function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

const bannerClass = "bg-[var(--naia-warn-bg)] text-foreground p-[10px_20px] text-[13px] font-semibold border-b border-border";

export default function ContentEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [doc, setDoc] = useState<Doc | null>(null);
  const [levels, setLevels] = useState<Ref[]>([]);
  const [subjects, setSubjects] = useState<Ref[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaved, setTitleSaved] = useState(false);
  const [sections, setSections] = useState<ContentSectionRow[]>([]);
  /* null = variante de référence — lot 2 (spec 13). Un examen à variantes
     reste éditable depuis cette bibliothèque, pas seulement depuis la page
     de structuration post-import. */
  const [activeVariantKey, setActiveVariantKey] = useState<string | null>(null);
  const [showSend, setShowSend] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genObjectives, setGenObjectives] = useState('');
  const [rubricVersion, setRubricVersion] = useState(0);
  /* Le travail de fond écrit les sections hors de tout flux SSE : à sa
     réussite, la liste se relit — c'est le remontage qui la recharge. */
  const [bootstrapVersion, setBootstrapVersion] = useState(0);
  const {
    job: bootstrapJob,
    showSuccess: bootstrapSuccess,
    retry: retryBootstrap,
    retrying: bootstrapRetrying,
    retryError: bootstrapRetryError,
    refresh: refreshBootstrapJob,
  } = useBootstrapJob(id, useCallback(() => setBootstrapVersion((v) => v + 1), []));
  const [pdfBusy, setPdfBusy] = useState<PdfJob | null>(null);
  const [previewPanel, setPreviewPanel] = useState<{ url: string; title: string } | null>(null);
  const [sidechatTab, setSidechatTab] = useState<'chat' | 'bareme'>('chat');

  const [sidechatOpen, setSidechatOpen] = useState(() => (typeof window !== 'undefined' ? window.localStorage.getItem(SIDECHAT_KEY) !== 'false' : true));
  useEffect(() => { window.localStorage.setItem(SIDECHAT_KEY, String(sidechatOpen)); }, [sidechatOpen]);

  const [sidechatWidth, setSidechatWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_SIDECHAT_WIDTH;
    const parsed = Number(window.localStorage.getItem(SIDECHAT_WIDTH_KEY));
    return parsed > 0 && Number.isFinite(parsed) ? clampSidechatWidth(parsed) : DEFAULT_SIDECHAT_WIDTH;
  });
  const [maxSidechatWidth, setMaxSidechatWidth] = useState(MAX_SIDECHAT_WIDTH);
  const corpsRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = corpsRef.current;
    if (!el) return;
    const applyAvailableWidth = () => {
      const available = clampSidechatWidth(el.clientWidth - RESIZE_HANDLE_WIDTH - CONTENT_MIN_WIDTH);
      setMaxSidechatWidth(available);
      setSidechatWidth((w) => Math.min(w, available));
    };
    applyAvailableWidth();
    const ro = new ResizeObserver(applyAvailableWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const resizeDragRef = useRef<{ startX: number; startW: number; currentW: number } | null>(null);
  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeDragRef.current = { startX: e.clientX, startW: sidechatWidth, currentW: sidechatWidth };
  };
  const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeDragRef.current) return;
    const newW = clampSidechatWidth(resizeDragRef.current.startW + (resizeDragRef.current.startX - e.clientX), maxSidechatWidth);
    resizeDragRef.current.currentW = newW;
    setSidechatWidth(newW);
  };
  const onResizePointerEnd = () => {
    if (resizeDragRef.current) window.localStorage.setItem(SIDECHAT_WIDTH_KEY, String(resizeDragRef.current.currentW));
    resizeDragRef.current = null;
  };
  const onResizeDoubleClick = () => {
    resizeDragRef.current = null;
    const w = clampSidechatWidth(DEFAULT_SIDECHAT_WIDTH, maxSidechatWidth);
    setSidechatWidth(w);
    window.localStorage.setItem(SIDECHAT_WIDTH_KEY, String(w));
  };

  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const activeEditorCtx = useMemo(() => ({ setActive: setActiveEditor }), [setActiveEditor]);

  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [attachingImport, setAttachingImport] = useState(false);
  const attachInputRef = useRef<HTMLInputElement>(null);

  /* Avertissements du dernier job d'import (visuel non extrait, conflit de
     barème…) — persistés en base côté job, jamais affichés avant ce lot
     (constat feasibility.md). Repris ici pour rester visibles au retour sur
     le document, pas seulement dans la modale d'import au moment du clic.
     Masqué manuellement, par document — pas de "lu" côté serveur. */
  const [importWarnings, setImportWarnings] = useState<readonly AuthoringWarningRow[]>([]);
  const [importWarningsDismissed, setImportWarningsDismissed] = useState(false);
  /* Corrigé importé sur un examen à variantes (≥2) : le chat classique
     n'écrit QUE la variante de référence (spec 13) — merger un corrigé
     multi-variantes par ce chemin laisse les variantes non-référence sans
     leurs propres answer/correction_rules, et l'éditeur affiche alors ceux
     de la référence par la fusion d'affichage (retour Ismaël, staging,
     lot 2). Le bouton générique « Ajouter un PDF/image » doit donc router
     vers le même job de fond que la modale d'import dédiée dès que le
     document a des variantes — jamais vers le chat dans ce cas. */
  const [answerKeyJobPhase, setAnswerKeyJobPhase] = useState<'idle' | 'running' | 'error'>('idle');
  const [answerKeyJobError, setAnswerKeyJobError] = useState<string | null>(null);
  const [answerKeyJobVersion, setAnswerKeyJobVersion] = useState(0);

  const archived = Boolean(doc?.archived_at);
  const assignable = doc?.kind === 'exercice_sheet';
  const editable = !archived;
  const total = sections.reduce((sum, s) => sum + sectionBareme(s), 0);

  const handlePublish = useCallback(async () => {
    if (!id || publishing || published) return;
    setPublishing(true);
    try {
      await api.teacher.publishDocument.mutate({ id });
      setPublished(true);
    } finally {
      setPublishing(false);
    }
  }, [id, publishing, published]);
  const hasAnswerKey = sections.some((s) => NOTED_TYPES.has(s.type));

  const loadDoc = useCallback(async () => {
    setError(null);
    try {
      const data = await api.content.get.query({ id });
      if (!data) { setError('Document introuvable.'); return; }
      setDoc(data as Doc);
      setTitleDraft(data.title);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { void loadDoc(); }, [loadDoc]);
  useEffect(() => {
    if (!id || doc?.kind !== 'exam') return;
    setImportWarningsDismissed(window.localStorage.getItem(`exam-import-warnings-dismissed:${id}`) === '1');
    api.content.examImport.status.query({ documentId: id })
      .then((job) => setImportWarnings((job as { warnings?: readonly AuthoringWarningRow[] | null } | null)?.warnings ?? []))
      .catch(() => setImportWarnings([]));
  }, [id, doc?.kind, answerKeyJobVersion]);
  const dismissImportWarnings = () => {
    if (id) window.localStorage.setItem(`exam-import-warnings-dismissed:${id}`, '1');
    setImportWarningsDismissed(true);
  };
  useEffect(() => {
    Promise.all([api.content.levels.query(), api.content.subjects.query()])
      .then(([lv, su]) => { setLevels(lv as Ref[]); setSubjects(su as Ref[]); }).catch(() => {});
  }, []);

  const labelOf = (list: Ref[], rid?: string) => list.find((r) => r.id === rid)?.label ?? '…';
  const chatDoc = useMemo(
    () => (doc ? { title: doc.title, level: labelOf(levels, doc.level_id), subject: labelOf(subjects, doc.subject_id), kind: doc.kind } : null),
    [doc, levels, subjects],
  );
  const {
    incomingSection, incomingCreated, incomingReordered, resourceChangedTick,
    messages, draft, setDraft, pendingImport, clearPendingImport,
    streaming, streamText, chatError, chatErrorIsPartialWrite,
    exhaustedNotice, truncatedNotice, onContinue,
    scrollRef: chatScrollRef, handleKey, send, sendText, retryLastMessage,
    refreshPendingImport: loadPendingImport,
  } = useDocumentChat({ documentId: id, doc: chatDoc, onBootstrapStarted: () => { void refreshBootstrapJob(); } });

  const saveTitle = async () => {
    if (!doc || titleDraft === doc.title) { setEditingTitle(false); return; }
    try {
      await api.content.update.mutate({ id, patch: { title: titleDraft } });
      setDoc((p) => p ? { ...p, title: titleDraft } : p);
      setTitleSaved(true);
      window.setTimeout(() => setTitleSaved(false), 2500);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setEditingTitle(false);
  };

  /* Le PDF est généré côté serveur (Chromium) puis servi via une URL signée. */
  const openPdf = async (variant: PdfVariant, mode: PdfMode) => {
    if (pdfBusy) return;
    setPdfBusy(`${variant}-${mode}`);
    setError(null);
    try {
      const { signed_url } = await api.content.pdf.mutate({ id, variant, variantKey: activeVariantKey });
      if (mode === 'view') {
        setPreviewPanel({ url: signed_url, title: pdfFileName(doc?.title ?? '', variant) });
        return;
      }
      /* `download` côté Supabase ⇒ Content-Disposition: attachment. */
      const a = window.document.createElement('a');
      a.href = `${signed_url}&download=${encodeURIComponent(pdfFileName(doc?.title ?? '', variant))}`;
      a.rel = 'noopener';
      window.document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfBusy(null);
    }
  };

  const toggleArchive = async () => {
    if (!doc) return;
    try {
      if (archived) await api.content.unarchive.mutate({ id }); else await api.content.archive.mutate({ id });
      setDoc((p) => p ? { ...p, archived_at: archived ? null : new Date().toISOString() } : p);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  /* Bouton « Ajouter un PDF/image » : réutilise le même import que le corrigé
     d'examen (upload signé + `content.importAnswerKey`), généralisé à tout
     document — l'import atterrit en pièce jointe en attente dans le chat. */
  const pollAnswerKeyJob = useCallback(() => {
    if (!id) return;
    const timer = window.setInterval(() => {
      api.content.examImport.status.query({ documentId: id })
        .then((job) => {
          const j = job as { status: 'running' | 'succeeded' | 'failed'; error: string | null } | null;
          if (!j || j.status === 'running') return;
          window.clearInterval(timer);
          if (j.status === 'succeeded') {
            setAnswerKeyJobPhase('idle');
            setAnswerKeyJobVersion((v) => v + 1);
          } else {
            setAnswerKeyJobPhase('error');
            setAnswerKeyJobError(j.error ?? 'La complétion du corrigé a échoué.');
          }
        })
        .catch(() => { /* un sondage perdu n'efface pas l'état déjà affiché */ });
    }, 2_000);
  }, [id]);

  const attachImport = async (file: File) => {
    if (!id || attachingImport) return;
    setAttachingImport(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop() ?? 'pdf';
      const mimeType = file.type || 'application/octet-stream';
      const upload = await api.content.importAnswerKey.mutate({ documentId: id, attachment: { ext, mimeType } });
      const res = await fetch(upload.signed_url, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file });
      if (!res.ok) throw new Error(`Échec de l'envoi de « ${file.name} » (${res.status})`);
      /* Classification auto du type — écrase silencieusement le choix initial
         de l'enseignant ; en cas d'échec le type initial reste en place. */
      await api.content.classifyKind.mutate({ documentId: id }).catch(() => {});
      if (doc?.variants && doc.variants.length >= 2) {
        /* Examen à variantes : le chat n'écrit que la référence (spec 13) —
           passe par le job de fond, comme la modale d'import dédiée. */
        setAnswerKeyJobPhase('running');
        setAnswerKeyJobError(null);
        await api.content.examImport.startAnswerKey.mutate({ documentId: id });
        pollAnswerKeyJob();
      } else {
        loadPendingImport();
        setSidechatOpen(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setAnswerKeyJobPhase('idle');
    } finally {
      setAttachingImport(false);
    }
  };

  const generateParcours = async () => {
    const obj = genObjectives.trim();
    if (!obj || streaming) return;
    setGenOpen(false);
    setGenObjectives('');
    setSidechatOpen(true);
    const ctx = doc ? `Niveau : ${labelOf(levels, doc.level_id)}, matière : ${labelOf(subjects, doc.subject_id)}.` : '';
    const prompt =
      `Génère un parcours interactif de 4 à 6 questions sur cet objectif : « ${obj} ». ${ctx}\n` +
      `Pour CHAQUE question, appelle create_section avec type "question_with_answer" et un content contenant : ` +
      `prompt (énoncé clair, RichDoc), answer (la réponse attendue, RichDoc), correction_rules (critères, RichDoc) et bareme (nombre). ` +
      `Progression du plus simple au plus complexe. Ne crée pas de sections de texte superflues.`;
    await sendText(prompt);
  };

  const sectionsRepo = useMemo(
    () => variantAwareSectionsRepository(id, activeVariantKey),
    [id, activeVariantKey],
  );
  const handleSectionsChange = useCallback(
    (next: ContentSectionRow[]) => { if (activeVariantKey === null) setSections(next); },
    [activeVariantKey],
  );

  if (loading) return (
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent width="full" showBurger backTo="/teacher/library" backLabel="Bibliothèque">{null}</HeaderContent>
      <p className="p-6 text-[var(--naia-text-secondary)]">Chargement…</p>
    </div>
  );
  if (!doc) return (
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent width="full" showBurger backTo="/teacher/library" backLabel="Bibliothèque">{null}</HeaderContent>
      <div className="p-6">
        <div className="text-[var(--naia-error)] font-bold">{error ?? 'Document introuvable.'}</div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/teacher/library')}>← Ma bibliothèque</Button>
      </div>
    </div>
  );

  const showSidechatColumn = sidechatOpen && !isMobile;
  const renameVariant = async (variantKey: string, label: string) => {
    try {
      await api.content.renameVariant.mutate({ documentId: id, variantKey, label });
      void loadDoc();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const chatProps = {
    messages, streaming, streamText, chatError, chatErrorIsPartialWrite, onRetry: retryLastMessage,
    exhaustedNotice, truncatedNotice, onContinue,
    draft, setDraft, handleKey, send, scrollRef: chatScrollRef,
    pendingImport, onClearPendingImport: clearPendingImport, isExam: doc.kind === 'exam',
    disabledReason: activeVariantKey !== null
      ? "Le chat modifie uniquement la variante de référence — éditez cette variante directement dans les sections."
      : null,
  };
  const evalPanelProps = {
    documentId: id, kind: doc.kind, sections, initialRules: doc.global_correction_rules ?? '', editable,
    onApplied: () => { setRubricVersion((v) => v + 1); void loadDoc(); },
  };

  return (
    <ActiveEditorCtx.Provider value={activeEditorCtx}>
    <div className="bg-[var(--naia-page)] flex flex-col" style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}>
      <HeaderContent
        width="full"
        showBurger
        backTo="/teacher/library"
        backLabel="Bibliothèque"
        actions={
          <>
            {(doc.kind === 'exam' || doc.kind === 'exercice_sheet') && total > 0 && (
              <button
                type="button"
                onClick={() => { setSidechatTab('bareme'); setSidechatOpen(true); }}
                className="text-[11px] font-extrabold bg-[var(--naia-sky)] text-naia-blue px-[9px] py-[3px] rounded-full whitespace-nowrap border-none cursor-pointer font-[inherit] hidden sm:inline-flex"
              >
                {total} pts
              </button>
            )}
            {!archived && sections.length > 0 && (
              <Button variant="outline" size="sm" disabled={pdfBusy !== null} onClick={() => void openPdf('subject', 'view')} className="hidden sm:inline-flex">
                {pdfBusy === 'subject-view' ? <CircleNotch size={14} className="animate-spin" /> : <Eye size={14} />} Aperçu
              </Button>
            )}
            {!archived && assignable && (
              <Button size="sm" onClick={() => setShowSend(true)}>Envoyer</Button>
            )}
            {!archived && sections.length > 0 && (
              <Button
                size="sm"
                variant={published ? 'outline' : 'default'}
                disabled={publishing || published}
                onClick={() => void handlePublish()}
                className={published ? 'text-green-600' : ''}
              >
                {publishing ? <CircleNotch size={14} className="animate-spin" /> : published ? <Check size={14} /> : null}
                {published ? 'Publié' : 'Publier'}
              </Button>
            )}
            {editable && (
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={attachingImport}
                onClick={() => attachInputRef.current?.click()}
                title="Importer un PDF/image"
                className="text-[var(--naia-text-secondary)]"
              >
                {attachingImport ? <CircleNotch size={15} className="animate-spin" /> : <UploadSimple size={15} />}
              </Button>
            )}
            {!isMobile && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setSidechatOpen((v) => !v)}
                title={sidechatOpen ? 'Masquer Naïa' : 'Afficher Naïa'}
                className={cn("transition-colors", sidechatOpen ? "text-naia-blue bg-[var(--naia-sky)]" : "text-[var(--naia-text-secondary)]")}
              >
                <SidebarSimple size={15} weight={sidechatOpen ? 'fill' : 'regular'} />
              </Button>
            )}
            <input
              ref={attachInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/heic,image/webp"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void attachImport(f);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }))} aria-label="Plus d'actions">
                <DotsThreeVertical size={16} weight="bold" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[210px]">
                {!archived && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>Rédaction</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => setTimeout(() => setGenOpen(true), 0)}>
                        <Sparkle size={15} /> Générer un parcours
                      </DropdownMenuItem>
                      {(doc.kind === 'exam' || doc.kind === 'exercice_sheet') && (
                        <DropdownMenuItem onClick={() => { setSidechatTab('bareme'); setSidechatOpen(true); }}>
                          <ChartBar size={15} /> Barème ({total} pts)
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Document</DropdownMenuLabel>
                  {editable && (
                    <DropdownMenuItem disabled={attachingImport} onClick={() => attachInputRef.current?.click()}>
                      {attachingImport ? <CircleNotch size={15} className="animate-spin" /> : <UploadSimple size={15} />}
                      {attachingImport ? 'Envoi…' : 'Ajouter un PDF/image'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem disabled={pdfBusy !== null} onClick={() => void openPdf('subject', 'view')}>
                    {pdfBusy === 'subject-view' ? <CircleNotch size={15} className="animate-spin" /> : <Eye size={15} />}
                    {pdfBusy === 'subject-view' ? 'Génération…' : 'Aperçu élève (PDF)'}
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={pdfBusy !== null} onClick={() => void openPdf('subject', 'download')}>
                    {pdfBusy === 'subject-download' ? <CircleNotch size={15} className="animate-spin" /> : <FilePdf size={15} />}
                    {pdfBusy === 'subject-download' ? 'Génération…' : 'Télécharger le sujet (PDF)'}
                  </DropdownMenuItem>
                  {hasAnswerKey && (
                    <DropdownMenuItem disabled={pdfBusy !== null} onClick={() => void openPdf('answer_key', 'download')}>
                      {pdfBusy === 'answer_key-download' ? <CircleNotch size={15} className="animate-spin" /> : <Key size={15} />}
                      {pdfBusy === 'answer_key-download' ? 'Génération…' : 'Télécharger le corrigé (PDF)'}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Danger</DropdownMenuLabel>
                  <DropdownMenuItem variant="destructive" onClick={() => void toggleArchive()}>
                    {archived ? <ArrowUUpLeft size={15} /> : <Archive size={15} />}
                    {archived ? 'Désarchiver' : 'Archiver'}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      >
        {null}
      </HeaderContent>

      {/* ── Rich-text toolbar mutualisée ── */}
      <div className={cn(
        "flex items-center bg-[var(--naia-card)] border-b border-[var(--naia-line)] flex-shrink-0 overflow-hidden transition-all duration-150",
        activeEditor ? "h-[36px]" : "h-0 border-b-0",
      )}>
        <div className="max-w-[760px] w-full mx-auto px-[32px] flex items-center gap-px overflow-x-auto">
          <RichToolbar editor={activeEditor} />
        </div>
      </div>

      {archived && <div className={bannerClass}>📦 Document archivé — lecture seule. Désarchivez-le pour le modifier.</div>}

      {!importWarningsDismissed && importWarnings.length > 0 && (
        <div className="p-[10px_20px] border-b border-border">
          <div className="max-w-[760px] mx-auto flex items-start gap-2">
            <AuthoringWarningsList warnings={importWarnings} className="flex-1" />
            <Button variant="ghost" size="sm" onClick={dismissImportWarnings}>Masquer</Button>
          </div>
        </div>
      )}

      {answerKeyJobPhase === 'running' && (
        <div className={cn(bannerClass, "flex items-center gap-2")}>
          <CircleNotch size={15} className="animate-spin" />
          Naïa complète le corrigé pour chaque variante…
        </div>
      )}
      {answerKeyJobPhase === 'error' && (
        <div className={cn(bannerClass, "flex items-center gap-2")}>
          {answerKeyJobError}
          <Button variant="ghost" size="sm" onClick={() => setAnswerKeyJobPhase('idle')}>Fermer</Button>
        </div>
      )}

      {/* ── Corps ── */}
      <div ref={corpsRef} className={cn("flex-1 flex overflow-hidden min-h-0", isMobile && "pb-[calc(52px+env(safe-area-inset-bottom))]")}>
        <div className="flex-1 min-w-0 overflow-y-auto p-[20px_20px_60px]">
          <div className="max-w-[760px] mx-auto bg-[var(--naia-card)] rounded-lg shadow-sm">

            {/* En-tête document : titre éditable + badges meta */}
            <div className="px-[32px] pt-[32px] pb-[4px]">
              {editingTitle && editable ? (
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => void saveTitle()}
                  onKeyDown={(e) => { if (e.key === 'Enter') void saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                  className="w-full text-[26px] font-black text-[var(--naia-text-primary)] leading-tight bg-transparent border-0 border-b-2 border-dashed border-naia-blue outline-none font-[inherit] m-0 p-0"
                />
              ) : (
                <h1
                  role="button"
                  tabIndex={editable ? 0 : undefined}
                  onClick={() => editable && setEditingTitle(true)}
                  onKeyDown={(e) => e.key === 'Enter' && editable && setEditingTitle(true)}
                  title={editable ? 'Cliquer pour renommer' : undefined}
                  className={cn(
                    "m-0 text-[26px] font-black text-[var(--naia-text-primary)] leading-tight",
                    editable && "cursor-pointer border-b-2 border-transparent hover:border-dashed hover:border-naia-blue transition-colors",
                  )}
                >
                  {doc.title}
                </h1>
              )}
              <div className="flex items-center justify-end gap-[6px] mt-[6px]">
                {titleSaved && (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-[var(--naia-green)] mr-auto animate-in fade-in duration-200">
                    <Check size={10} weight="bold" /> Sauvegardé
                  </span>
                )}
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-60 font-medium">{KIND_LABELS[doc.kind]}</span>
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-30">·</span>
                <span className="text-[10px] text-[var(--naia-text-secondary)] opacity-60 font-medium">{labelOf(levels, doc.level_id)} · {labelOf(subjects, doc.subject_id)}</span>
              </div>
              <div className="mt-[14px] border-b border-[var(--naia-line)]" />
            </div>

            {doc.variants && doc.variants.length >= 2 && (
              <VariantTabs variants={doc.variants} activeKey={activeVariantKey} onSelect={setActiveVariantKey} onRename={(k, l) => void renameVariant(k, l)} />
            )}
            <div className="p-[16px_32px_32px]">
              {bootstrapJob && (
                <BootstrapProgress
                  job={bootstrapJob}
                  showSuccess={bootstrapSuccess}
                  onRetry={() => void retryBootstrap()}
                  retrying={bootstrapRetrying}
                  retryError={bootstrapRetryError}
                />
              )}
              <ContentSectionsEditor
                key={`${rubricVersion}-${bootstrapVersion}-${answerKeyJobVersion}`} documentId={id} editable={editable}
                structureEditable={activeVariantKey === null}
                repository={sectionsRepo}
                incomingSection={activeVariantKey === null ? incomingSection : null}
                incomingCreated={activeVariantKey === null ? incomingCreated : null}
                incomingReordered={activeVariantKey === null ? incomingReordered : null}
                resourceChangedTick={activeVariantKey === null ? resourceChangedTick : undefined}
                onSectionsChange={handleSectionsChange} />
            </div>
          </div>
        </div>

        {showSidechatColumn && (
          <>
            <div
              onPointerDown={onResizePointerDown}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerEnd}
              onPointerCancel={onResizePointerEnd}
              onDoubleClick={onResizeDoubleClick}
              title="Glisser pour redimensionner · double-clic pour réinitialiser"
              className="w-[11px] shrink-0 cursor-col-resize touch-none flex justify-center group"
            >
              <div className="w-[3px] h-full bg-[var(--naia-line)] group-hover:bg-naia-blue transition-colors" />
            </div>
            <div
              data-sidechat="desktop"
              className="flex flex-col min-h-0 min-w-0 bg-[var(--naia-card)] border-l border-[var(--naia-line)]"
              style={{ flexBasis: sidechatWidth, flexShrink: 0 }}
            >
              {/* Onglets Chat / Barème */}
              <div className="flex border-b border-[var(--naia-line)] bg-[var(--naia-card-alt)] flex-shrink-0">
                {(['chat', 'bareme'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSidechatTab(tab)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-[5px] py-[9px] text-[12px] font-bold border-b-2 transition-colors font-[inherit] bg-transparent border-x-0 border-t-0 cursor-pointer",
                      sidechatTab === tab
                        ? "border-naia-blue text-naia-blue"
                        : "border-transparent text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]",
                    )}
                  >
                    {tab === 'chat' ? <ChatCircle size={14} /> : <ChartBar size={14} />}
                    {tab === 'chat' ? 'Naïa' : 'Barème'}
                  </button>
                ))}
              </div>
              {sidechatTab === 'chat'
                ? <DocumentSidechat {...chatProps} />
                : <div className="flex-1 overflow-y-auto p-4"><EvaluationPanel {...evalPanelProps} /></div>
              }
            </div>
          </>
        )}
      </div>

      {/* ── Mobile : bottom bar ── */}
      {isMobile && !sidechatOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--naia-card)] border-t border-[var(--naia-line)] px-3 pt-2 pb-[calc(8px+env(safe-area-inset-bottom))] flex gap-2">
          {!archived && sections.length > 0 && <Button variant="outline" className="flex-1 text-[13px]" size="sm" disabled={pdfBusy !== null} onClick={() => void openPdf('subject', 'view')}>{pdfBusy === 'subject-view' ? <CircleNotch size={14} className="animate-spin" /> : <Eye size={14} />} Aperçu</Button>}
          {!archived && assignable && <Button className="flex-1 text-[13px]" size="sm" onClick={() => setShowSend(true)}>Envoyer</Button>}
          <Button variant="outline" size="icon-sm" onClick={() => setSidechatOpen(true)} aria-label="Ouvrir Naïa">
            <ChatCircle size={16} />
          </Button>
        </div>
      )}

      {/* ── Mobile : drawer Naïa ── */}
      {isMobile && sidechatOpen && (
        <div data-sidechat="mobile" className="fixed inset-0 z-50 flex flex-col min-w-0 bg-[var(--naia-card)]">
          <div className="flex items-center gap-2 px-4 border-b border-[var(--naia-line)] bg-[var(--naia-card-alt)] flex-shrink-0">
            {(['chat', 'bareme'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setSidechatTab(tab)}
                className={cn(
                  "flex items-center gap-[5px] py-[11px] text-[12px] font-bold border-b-2 transition-colors font-[inherit] bg-transparent border-x-0 border-t-0 cursor-pointer",
                  sidechatTab === tab
                    ? "border-naia-blue text-naia-blue"
                    : "border-transparent text-[var(--naia-text-secondary)]",
                )}
              >
                {tab === 'chat' ? <ChatCircle size={14} /> : <ChartBar size={14} />}
                {tab === 'chat' ? 'Naïa' : 'Barème'}
              </button>
            ))}
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setSidechatOpen(false)}>Fermer</Button>
          </div>
          {sidechatTab === 'chat'
            ? <DocumentSidechat {...chatProps} />
            : <div className="flex-1 overflow-y-auto p-4"><EvaluationPanel {...evalPanelProps} /></div>
          }
        </div>
      )}

      {showSend && (
        <SendAssignmentModal
          documentId={id}
          docTitle={doc.title}
          onClose={() => setShowSend(false)}
          onSent={(n) => {
            setShowSend(false);
            setSentMsg(`Devoir envoyé à ${n} élève${n > 1 ? 's' : ''}.`);
            window.setTimeout(() => setSentMsg(null), 4000);
          }}
        />
      )}
      {sentMsg && <div className={cn(bannerClass, "fixed bottom-4 left-1/2 -translate-x-1/2 z-[1200] border-b-0 rounded-sm shadow-card")}>✓ {sentMsg}</div>}
      {previewPanel && (
        <PdfPanel url={previewPanel.url} title={previewPanel.title} onClose={() => setPreviewPanel(null)} />
      )}
      <Dialog open={genOpen} onOpenChange={(open) => { if (!open) setGenOpen(false); }}>
        <DialogContent style={{ maxWidth: 460 }}>
          <DialogHeader>
            <DialogTitle>Générer un parcours interactif</DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 flex flex-col gap-3">
            <p className="m-0 text-[14px] text-[var(--naia-text-secondary)]">
              Décrivez l'objectif : Naïa crée une suite de questions (énoncé, réponse, barème) que vous pourrez modifier.
            </p>
            <Textarea
              value={genObjectives}
              onChange={(e) => setGenObjectives(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Ex. : additionner des fractions à dénominateurs différents"
            />
          </div>
          <div className="flex justify-end gap-2 px-5 pb-5 pt-1">
            <Button variant="outline" size="sm" onClick={() => setGenOpen(false)}>Annuler</Button>
            <Button disabled={!genObjectives.trim() || streaming} onClick={() => void generateParcours()}>
              {streaming ? 'Naïa génère…' : 'Générer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </ActiveEditorCtx.Provider>
  );
}

